/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { colors } from 'styles/variables';
import setBlockProps from 'services/apiHelpers/setBlockProps';
// updated UI
import { H1 as H1_v2, H2 as H2_v2 } from '../EditorUtilities/UI/Headings';
import TogglePanel from '../EditorUtilities/UI/TogglePanel';
import CheckBox_v2 from '../EditorUtilities/UI/CheckBox';
import ColorPicker_v2 from '../EditorUtilities/UI/ColorPicker';
import pubSub from 'services/pubSub';
import BigSwitch from 'components/UI/BigSwitch';
import NumberPicker_v2 from '../EditorUtilities/UI/NumberPicker';
import Info from '../EditorUtilities/UI/Info';
import InputText from 'components/UI/InputText';

const { mint, purple } = colors;

const togglePanelsCache = {};

interface HProps {
  children: any;
  style?: object;
}

export const scorePercentDefaultText = 'You got {{score}}%';

export const attemptCounterDefaultText =
  'Attempt {{current_attempt}} of {{total_attempts}}';

const EditAssessmentResults = ({ block }: { block?: any }) => {
  //
  const { id: blockId } = block;
  const {
    graphColor,
    resultsPercentTextColor,
    showAttemptCounter,
    attemptCounterText,
    attemptCounterTextColor,
    questionTitleColor = '#000',
    showFeedbackText,
    enableLearnMore,
    enableTryAgain,
    enableReVisit,
    includeResultsGraph,
    scorePercentText,
    addAssessmentRetry,
    enableBranching,
    lockDown,
    //
    backgroundPanelColor = '#fff',
    backgroundPanelBorderRadius = 20,
    backgroundPanelDropShadow = true,

    customButtonBorderRadius = false,
    buttonBorderRadius = 18,

    learnMoreLabel,
    tryAgainLabel,
    revisitLabel,
  } = block.props;

  const correctColor = block.props.correctColor || mint;
  const incorrectColor = block.props.incorrectColor || purple;

  const [topic, setTopic] = useState(null);
  const [showCorrect, setShowCorrect] = useState<boolean>(false);
  const [imageLibrary, setImageLibrary] = useState(false);

  const saveTogglePanelState = (panel, open) => {
    togglePanelsCache[blockId] = {
      ...togglePanelsCache[blockId],
      [panel]: open,
    };

    setBlockProps(block, { ...block.props });
  };

  const showFeedback = useRef<boolean>(false);

  const setShowFeedback = toggle => {
    showFeedback.current = toggle;
    pubSub.publish(`SHOW_FEEDBACK_${blockId}`, {
      showFeedback: showFeedback.current,
    });
  };

  useEffect(() => {
    pubSub.publish(`RESULTS_SHOW_CORRECT_${blockId}`, {
      showCorrect,
    });
  }, [showCorrect]);

  return (
    <_>
      <div
        style={{
          width: 'calc(100% - 30px)',
          margin: '0 0 20px 0',
        }}
      >
        <BigSwitch
          leftItem="Show correct"
          rightItem="Show incorrect"
          leftValue={'true'}
          rightValue={'false'}
          value={showCorrect ? 'true' : 'false'}
          callback={value => {
            setShowFeedback(false);
            setShowCorrect(value === 'true');
          }}
        />
      </div>
      <TogglePanel
        label={<H1_v2>interaction: settings</H1_v2>}
        toggle={
          togglePanelsCache[blockId]
            ? togglePanelsCache[blockId]['interaction: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('interaction: settings', open)}
      >
        <CheckBox_v2
          label="Include results percent graph"
          on={includeResultsGraph}
          onClick={() =>
            setBlockProps(block, {
              includeResultsGraph: !includeResultsGraph,
            })
          }
        />
        {includeResultsGraph && (
          <div>
            <div
              style={{
                margin: '0 0 10px 0',
              }}
            >
              <ColorPicker_v2
                key="resultsPercentTextColor"
                inColor={resultsPercentTextColor || '#000'}
                outColor={color =>
                  setBlockProps(block, {
                    resultsPercentTextColor: color ? color : '#000',
                  })
                }
                clearedColor="#000"
                label="Results percent text colour"
              />
            </div>

            <div
              style={{
                margin: '0 0 10px 0',
              }}
            >
              <Info
                info={
                  <_>
                    <div>
                      <div>
                        {`You can add any text you like here, but the "{{score}}"
                  variable will be replaced with the actual value:`}
                      </div>
                      <div
                        style={{
                          margin: '10px 0 0 0',
                        }}
                      >
                        {`
This variable will need to be included in the text you enter, so, for
                example, if you want the text to read "You got 100%", you
                would enter "You got {{score}}%".`}
                      </div>

                      <div
                        style={{
                          margin: '10px 0 0 0',
                        }}
                      >
                        {`The "{{" and "}}" are important, so
                please remember to include them.`}
                      </div>
                    </div>
                  </_>
                }
              >
                Results percentage text
              </Info>

              <InputText
                defaultValue={scorePercentText || scorePercentDefaultText}
                onUpdate={value => {
                  setBlockProps(block, {
                    scorePercentText: value,
                  });
                }}
                onEnter={value => console.log(value)}
                onBlur={value => {
                  if (!value) {
                    setBlockProps(block, {
                      scorePercentText: scorePercentDefaultText,
                    });
                  }
                }}
                style={{ margin: '0 0 0 0' }}
              />
            </div>
            <ColorPicker_v2
              inColor={graphColor}
              outColor={color => setBlockProps(block, { graphColor: color })}
              label="Results percent graph colour"
            />
          </div>
        )}
        <CheckBox_v2
          key="showAttemptCounter"
          label="Show attempts"
          // for all questions
          on={showAttemptCounter}
          onClick={() =>
            setBlockProps(block, {
              showAttemptCounter: !showAttemptCounter,
            })
          }
        />

        {showAttemptCounter && (
          <_>
            <div
              style={{
                margin: '0 0 10px 0',
              }}
            >
              <Info
                info={
                  <_>
                    <div>
                      <div>
                        You can add any text you like here, but the following
                        variables will be replaced with the actual values:
                        <ul
                          style={{
                            margin: '0 0 0 20px',
                          }}
                        >
                          <li>
                            <strong>{`{{current_attempt}}`}</strong> - The
                            current attempt number
                          </li>
                          <li>
                            <strong>{`{{total_attempts}}`}</strong> - The total
                            number of attempts allowed
                          </li>
                        </ul>
                      </div>
                      <div
                        style={{
                          margin: '10px 0 0 0',
                        }}
                      >
                        {`
Those variables will need to be included in the text you enter, so, for
                example, if you want the text to read "Attempt 1 of 2", you
                would enter "Attempt {{current_attempt}} of
                {{total_attempts}}".`}
                      </div>

                      <div
                        style={{
                          margin: '10px 0 0 0',
                        }}
                      >
                        {`The "{{" and "}}" are important, so
                please remember to include them.`}
                      </div>
                    </div>
                  </_>
                }
              >
                Attempt counter text
              </Info>

              <InputText
                defaultValue={attemptCounterText || attemptCounterDefaultText}
                onUpdate={value => {
                  setBlockProps(block, {
                    attemptCounterText: value,
                  });
                }}
                onEnter={value => console.log(value)}
                onBlur={value => {
                  if (!value) {
                    setBlockProps(block, {
                      attemptCounterText: attemptCounterDefaultText,
                    });
                  }
                }}
                style={{ margin: '0 0 0 0' }}
              />
            </div>

            <div>
              <ColorPicker_v2
                key="attemptCounterTextColor"
                inColor={attemptCounterTextColor || '#000'}
                outColor={color =>
                  setBlockProps(block, {
                    attemptCounterTextColor: color ? color : '#000',
                  })
                }
                clearedColor="#000"
                label="Attempt counter text colour"
              />
            </div>
          </_>
        )}

        <div>
          <ColorPicker_v2
            inColor={questionTitleColor}
            outColor={color =>
              setBlockProps(block, { questionTitleColor: color })
            }
            label="Question title colour"
          />
        </div>

        <div>
          <ColorPicker_v2
            inColor={backgroundPanelColor}
            outColor={color =>
              setBlockProps(block, { backgroundPanelColor: color })
            }
            label="Background panel colour"
          />
        </div>
        <div>
          <NumberPicker_v2
            label="Background panel border radius"
            min={0}
            value={backgroundPanelBorderRadius}
            onChange={value => {
              setBlockProps(block, { backgroundPanelBorderRadius: value });
            }}
          />
        </div>
        <div>
          <CheckBox_v2
            label="Background panel drop shadow"
            on={backgroundPanelDropShadow}
            onClick={() =>
              setBlockProps(block, {
                backgroundPanelDropShadow: !backgroundPanelDropShadow,
              })
            }
          />
        </div>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CheckBox_v2
            on={customButtonBorderRadius}
            onClick={() =>
              setBlockProps(block, {
                customButtonBorderRadius: !customButtonBorderRadius,
              })
            }
          />
          <Info
            info={
              <div>
                Enable this option if you'd like to customise the border radius
                of the Learn more, Try again and Revisit buttons.
              </div>
            }
          >
            <div style={{ margin: '0 0 0 10px' }}>
              Custom button border radius
            </div>
          </Info>
        </div>

        {customButtonBorderRadius ? (
          <div>
            <NumberPicker_v2
              label="Button border radius"
              min={0}
              value={buttonBorderRadius}
              onChange={value => {
                setBlockProps(block, { buttonBorderRadius: value });
              }}
            />
          </div>
        ) : null}

        <div>
          <H1_v2 style={{ margin: '0 0 5px 0' }}>Button labels</H1_v2>

          <H2_v2>Learn more</H2_v2>
          <InputText
            defaultValue={learnMoreLabel || 'Learn more'}
            onUpdate={value => {
              setBlockProps(block, { learnMoreLabel: value });
            }}
            onEnter={value => console.log(value)}
            style={{ margin: '0 0 0 0' }}
          />

          <H2_v2 style={{ margin: '5px 0 0 0' }}>Try again</H2_v2>
          <InputText
            defaultValue={tryAgainLabel || 'Try again'}
            onUpdate={value => {
              setBlockProps(block, { tryAgainLabel: value });
            }}
            onEnter={value => console.log(value)}
            style={{ margin: '0 0 0 0' }}
          />

          <H2_v2 style={{ margin: '5px 0 0 0' }}>Revisit</H2_v2>
          <InputText
            defaultValue={revisitLabel || 'Revisit'}
            onUpdate={value => {
              setBlockProps(block, { revisitLabel: value });
            }}
            onEnter={value => console.log(value)}
            style={{ margin: '0 0 0 0' }}
          />
        </div>
      </TogglePanel>

      <TogglePanel
        label={<H1_v2>results: settings</H1_v2>}
        toggle={
          togglePanelsCache[blockId]
            ? togglePanelsCache[blockId]['results: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('results: settings', open)}
      >
        <CheckBox_v2
          key="showFeedbackText"
          label="Show feedback"
          on={showFeedbackText}
          onClick={() =>
            setBlockProps(block, {
              showFeedbackText: !showFeedbackText,
            })
          }
        />

        {showCorrect
          ? [
              <ColorPicker_v2
                key="correctColor"
                inColor={correctColor}
                outColor={color =>
                  setBlockProps(block, { correctColor: color })
                }
                label="Correct colour"
              />,
              <CheckBox_v2
                key="correctColor"
                label='Add "Re-visit" when correct'
                on={enableReVisit}
                onClick={() =>
                  setBlockProps(block, {
                    enableReVisit: !enableReVisit,
                  })
                }
              />,
            ]
          : [
              <ColorPicker_v2
                key="incorrectColor"
                inColor={incorrectColor}
                outColor={color =>
                  setBlockProps(block, { incorrectColor: color })
                }
                label="Incorrect colour"
              />,

              <CheckBox_v2
                key="enableLearnMore"
                label='Add "Learn more" when incorrect'
                // for all questions
                on={enableLearnMore}
                onClick={() =>
                  setBlockProps(block, {
                    enableLearnMore: !enableLearnMore,
                  })
                }
              />,

              <CheckBox_v2
                key="enableTryAgain"
                label='Add "Try again" when incorrect'
                on={enableTryAgain}
                onClick={() =>
                  setBlockProps(block, {
                    enableTryAgain: !enableTryAgain,
                  })
                }
              />,
            ]}

        <CheckBox_v2
          label="Add assessment retry"
          on={addAssessmentRetry}
          onClick={() =>
            setBlockProps(block, {
              addAssessmentRetry: !addAssessmentRetry,
            })
          }
        />
        <CheckBox_v2
          label="Enable branching for a passed or failed result"
          on={enableBranching}
          onClick={() =>
            setBlockProps(block, {
              enableBranching: !enableBranching,
            })
          }
        />
        <CheckBox_v2
          label="Lock down"
          on={!!lockDown}
          onClick={() => setBlockProps(block, { lockDown: !!lockDown ? 0 : 1 })}
        />
      </TogglePanel>
    </_>
  );
};

export default EditAssessmentResults;
